import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import { ShortsCarousel } from '@quintype/arrow';
import { ShortsAdWidget } from './shorts-ad-widget';

import './shorts.m.css';

export const ShortsPage = ({ data = {}, config = {} }) => {
  const enableDarkMode = useSelector(state => get(state, ['header', 'isDarkModeEnabled'], false));
  const shortsPrefix = get(config, ['pagebuilder-config', 'featureToggle', 'configureShortsRoute'], 'shorts').replace(/^\//, '');
  const returnUrlRef = useRef(null);

  const shortsEnabled = get(config, ['pagebuilder-config', 'featureToggle', 'shortsPage'], false);
  const { stories = [], startIndex = 0, entryStory, collectionSlug, childCollectionsWithMore = [], initialTopLevelOffset } = data;

  const widgetCode = get(config, ['pagebuilder-config', 'shorts', 'widgetCode'], null);
  const targetingId = get(config, ['pagebuilder-config', 'shorts', 'targetingId'], null);
  const mobilePlaceholderHeight = Math.min(Number(get(config, ['pagebuilder-config', 'shorts', 'mobilePlaceholderHeight'], '')), 600);
  const desktopPlaceholderHeight = Math.min(Number(get(config, ['pagebuilder-config', 'shorts', 'desktopPlaceholderHeight'], '')), 600);

  const adConfig = useMemo(() => {
    return widgetCode
      ? {
        enabled: true,
        adComponent: () => (
          <ShortsAdWidget
            widgetCode={widgetCode}
            targetingId={targetingId}
            mobilePlaceholderHeight={mobilePlaceholderHeight}
            desktopPlaceholderHeight={desktopPlaceholderHeight}
            enableDarkMode={enableDarkMode}
          />
        )
      }
      : { enabled: false };
  }, [widgetCode, targetingId, mobilePlaceholderHeight, desktopPlaceholderHeight, enableDarkMode]);

  const handleStoryChange = useCallback((story) => {
    if (typeof window === 'undefined') return;
    window.history.replaceState(null, story.headline, `/${shortsPrefix}/${story.slug}`);
  }, [shortsPrefix]);

  const handleClose = useCallback(() => {
    window.location.href = returnUrlRef.current || '/';
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const storedReturnUrl = window.sessionStorage.getItem('shortsReturnUrl');
    if (storedReturnUrl) {
      returnUrlRef.current = storedReturnUrl;
      window.sessionStorage.removeItem('shortsReturnUrl');
      return;
    }

    const referrer = document.referrer;
    if (referrer) {
      try {
        const referrerUrl = new URL(referrer);
        const isSameOrigin = referrerUrl.origin === window.location.origin;
        const isShorts = referrerUrl.pathname.startsWith(`/${shortsPrefix}`);
        if (isSameOrigin && !isShorts) {
          returnUrlRef.current = referrerUrl.pathname + referrerUrl.search + referrerUrl.hash;
        }
      } catch (err) {
        console.error("Failed to parse referrer URL in Shorts component:", err);
      }
    }
  }, []);

  if (!shortsEnabled) return null;
  if (!stories.length) return null;

  const logo = get(config, ['pagebuilder-config', 'header', 'logo'], '');
  const darkLogo = get(config, ['pagebuilder-config', 'header', 'darkLogo'], logo);
  const logoAltText = get(config, ['pagebuilder-config', 'general', 'logoAltText'], '') || get(config, ['publisher-name'], '');
  const activeLogo = enableDarkMode ? darkLogo : logo;
  const readFullStoryButton = get(config, ['pagebuilder-config', 'shorts', 'readFullStoryButton'], 'Read Full Story');
  const allCaughtUpTitle = get(config, ['pagebuilder-config', 'shorts', 'allCaughtUpText']) || 'You are all caught up';
  const allCaughtUpMessage = get(config, ['pagebuilder-config', 'shorts', 'allStoriesReadText']) || "That's all for now. More stories will appear here soon.";
  const timeSpentLabel = get(config, ['pagebuilder-config', 'shorts', 'timeSpentText']) || 'Time Spent';
  const storiesReadLabel = get(config, ['pagebuilder-config', 'shorts', 'storiesReadText']) || 'Stories Read';

  return (
    <div styleName={`shorts-page-wrapper${enableDarkMode ? ' shorts-page-wrapper--dark' : ''}`}>
      <div styleName={`shorts-navbar${enableDarkMode ? ' shorts-navbar--dark' : ''}`}>
        <span aria-hidden="true" />
        {activeLogo && (
          <img
            className="logoHeight"
            src={activeLogo}
            alt={logoAltText}
          />
        )}
        <button styleName="shorts-close-btn" onClick={handleClose} aria-label="Close shorts">
          &times;
        </button>
      </div>
      <ShortsCarousel
        stories={stories}
        startIndex={startIndex}
        entryStory={entryStory}
        config={config}
        shortsPrefix={shortsPrefix}
        readFullStoryButton={readFullStoryButton}
        enableDarkMode={enableDarkMode}
        onStoryChange={handleStoryChange}
        adConfig={adConfig}
        allCaughtUpTitle={allCaughtUpTitle}
        allCaughtUpMessage={allCaughtUpMessage}
        timeSpentLabel={timeSpentLabel}
        storiesReadLabel={storiesReadLabel}
        collectionSlug={collectionSlug}
        childCollectionsWithMore={childCollectionsWithMore}
        initialTopLevelOffset={initialTopLevelOffset}
      />
    </div>
  );
};
